import axios from "axios";
import { getSession, sendMessageToExtension, setSession } from "./util";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false; // Flag to track ongoing token refresh
let refreshSubscribers: (() => void)[] = []; // Array to hold subscribers waiting for token refresh

// Function to refresh the access token
const refreshToken = async () => {
  try {
    const session = getSession();
    if (session && session.refreshToken) {
      api.defaults.headers.common["refresh-token"] = session.refreshToken;
      const response: { access_token: string } = await api.post(
        `/refresh-token`
      );
      delete api.defaults.headers.common["refresh-token"];
      const accessToken = response.access_token;
      const newSession = {
        ...session,
        accessToken,
      };
      setSession(newSession);
      sendMessageToExtension({
        action: "LOGIN",
        payload: newSession,
      });
      return accessToken;
    }
  } catch (error) {
    console.log("Error while refreshing", error);
    setSession(null);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

const handle401Error = async (error: any) => {
  const originalRequest = error.config;
  if (!isRefreshing) {
    isRefreshing = true;
    await refreshToken();
    onAccessTokenRefreshed();
    return api(originalRequest);
  } else {
    // Create a promise that will resolve
    const retryOriginalRequest = new Promise((resolve, reject) => {
      // Add the resolve function to the subscribers array
      refreshSubscribers.push(() => {
        resolve(api(originalRequest));
      });
    });
    return retryOriginalRequest;
  }
};

// Function to notify all subscribers
const onAccessTokenRefreshed = () => {
  refreshSubscribers.forEach((subscriber) => subscriber());
  refreshSubscribers = [];
};

export const handleLogout = async () => {
  try {
    api.delete(`/logout`);
  } catch (logoutError) {
    console.log("Error while logout", logoutError);
  }
  logout();
};

const logout = () => {
  setSession(null);
  sendMessageToExtension({
    action: "LOGOUT",
  });
  delete api.defaults.headers.common.Authorization;
  window.location.href = `/login?redirectUrl=${window.location.pathname}`;
};

api.interceptors.request.use(async (config) => {
  const session = getSession();
  if (session && config.headers) {
    config.headers.Authorization = session.accessToken;
    api.defaults.headers.common.Authorization = session.accessToken;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const session = getSession();
    if (error.response && session) {
      if (error.response.status === 401) {
        return await handle401Error(error);
      }
      if (error.response.status === 403) {
        await handleLogout();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
